import { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { Search } from 'lucide-react';

export default function CreateDMModal() {
    const { showCreateDM, setShowCreateDM, createDM, users, currentUser, hasPermission } = useApp();
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    const isAdmin = hasPermission('admin:view') || hasPermission('super:admin');
    const filteredUsers = users.filter(u =>
        (u.id !== currentUser?.id || isAdmin) &&
        (u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (u.email && u.email.toLowerCase().includes(searchQuery.toLowerCase())))
    );

    const handleClose = () => {
        setShowCreateDM(false);
        setSelectedUsers([]);
        setSearchQuery('');
        setError('');
    };

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') handleClose();
        };
        if (showCreateDM) document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [showCreateDM]);

    if (!showCreateDM) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (selectedUsers.length === 0) return;

        setIsSubmitting(true);
        try {
            setError('');
            await createDM(selectedUsers);
            setSelectedUsers([]);
            setSearchQuery('');
        } catch (err: any) {
            console.error('Failed to create DM:', err);
            setError(err.message || 'Failed to start direct message');
        }
        setIsSubmitting(false);
    };

    const toggleUser = (userId: string) => {
        // Enforce single selection: if already selected, deselect; otherwise, replace selection
        setSelectedUsers(prev => prev.includes(userId) ? [] : [userId]);
    };

    const getInitials = (name: string) => {
        return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
    };

    return (
        <div className="modal-overlay" onClick={handleClose}>
            <div className="modal" style={{ display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
                <header className="modal-header">
                    <h2 className="modal-title">New message</h2>
                    <button className="btn btn-ghost btn-icon" onClick={handleClose}>✕</button>
                </header>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                    {error && (
                        <div className="auth-error" style={{ margin: 'var(--spacing-md) var(--spacing-md) 0' }}>
                            {error}
                        </div>
                    )}
                    <div className="modal-body" style={{ flex: 1, overflowY: 'auto' }}>
                        <div className="modal-search-wrapper">
                            <Search size={16} className="modal-search-icon" />
                            <input
                                className="modal-search-input"
                                placeholder="Search for a person..."
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                autoFocus
                            />
                        </div>

                        <div className="modal-list-container">
                            {filteredUsers.length === 0 ? (
                                <div className="empty-state" style={{ padding: '20px' }}>
                                    <div className="empty-state-text" style={{ marginBottom: 0 }}>
                                        No users found.
                                    </div>
                                </div>
                            ) : (
                                filteredUsers.map(user => {
                                    const isSelected = selectedUsers.includes(user.id);
                                    return (
                                        <div
                                            key={user.id}
                                            onClick={() => toggleUser(user.id)}
                                            className={`user-list-item ${isSelected ? 'active' : ''}`}
                                        >
                                            <div className="avatar avatar-sm" style={{ position: 'relative' }}>
                                                {getInitials(user.name)}
                                                <span className={`status-dot ${user.status}`} />
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontWeight: 500, fontSize: 'var(--font-size-sm)' }}>{user.name}</div>
                                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                                                    {user.status === 'active' ? 'Active' : 'Away'}
                                                </div>
                                            </div>
                                            {isSelected && <span style={{ marginLeft: 'auto', color: 'var(--accent-primary)', fontWeight: 'bold' }}>✓</span>}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>

                    <div className="modal-footer" style={{ flexShrink: 0 }}>
                        <button type="button" className="btn btn-secondary" onClick={handleClose}>
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={selectedUsers.length === 0 || isSubmitting}
                        >
                            {isSubmitting ? 'Starting...' : 'Start Conversation'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
